/**
 * Connection map for the RetailCircle design system.
 *
 * A snapshot of how each shared primitive / Button variant is wired into the
 * app today, so a structural change can be made in ONE place with full
 * visibility on what it ripples to. Rendered by StyleGuidePage.jsx.
 *
 * status legend:
 *   shared     — used in multiple call sites; change here affects many screens
 *   standalone — exactly one call site; safe-ish to change in isolation
 *   unused     — a real design-system option with zero call sites today
 *   internal   — no direct JSX call sites, but a dependency of another primitive
 *                (NOT dead — deleting it breaks its consumer)
 *   orphaned   — component defined in the DS but bypassed everywhere (dead)
 *   legacy     — a fork living OUTSIDE the DS that never adopted the primitive
 *
 * HOW TO REFRESH: these counts come from a call-site sweep of src/**\/*.{jsx,js}
 * (excluding definition files + barrels + DESIGN-SYSTEM.md). Re-run that sweep
 * after a refactor and update the `sites` arrays below.
 */

const statusFor = (count) => (count > 1 ? "shared" : count === 1 ? "standalone" : "unused");

// ── Button variants ──────────────────────────────────────────────
export const BUTTON_VARIANTS = [
  {
    variant: "primary",
    note: "Default when no variant is passed. All page-header CTAs.",
    sites: [
      "listings/atoms/LookbookTopButtons.jsx",
      "buyAlerts/RCBuyAlertsPage.jsx",
      "listings/pages/RCListingsPage.jsx",
      "listings/pages/RCLookbooksPage.jsx",
      "stock/templates/CoreListTemplate.jsx",
      "views/Orders/ordersTabs.js",
      "views/UsersPage.jsx",
    ],
  },
  {
    variant: "secondary",
    sites: [
      "buyAlerts/components/FilterForms.jsx",
      "listings/pages/RCListingsPage.jsx",
    ],
  },
  {
    variant: "outline",
    sites: ["listings/pages/RCListingsPage.jsx"],
  },
  { variant: "ghost", sites: [] },
  { variant: "danger", sites: [] },
  {
    variant: "success",
    sites: ["buyAlerts/components/FilterForms.jsx"],
  },
  {
    variant: "tab",
    sites: [
      "stock/components/CoreListFilterPills.jsx",
      "stock/components/TimeWindowPills.jsx",
    ],
  },
  { variant: "icon", sites: [] },
].map((v) => ({ ...v, count: v.sites.length, status: statusFor(v.sites.length) }));

// ── The CTA hero modifier (className, not a variant) ──────────────
export const HERO_MODIFIER = {
  name: "rc-ui-btn--hero",
  note: 'variant="primary" + size="lg" + this class. The standardised page-header CTA.',
  sites: [
    "buyAlerts/RCBuyAlertsPage.jsx",
    "listings/pages/RCListingsPage.jsx",
    "listings/pages/RCLookbooksPage.jsx",
    "stock/templates/CoreListTemplate.jsx",
    "views/Orders/ordersTabs.js",
    "views/UsersPage.jsx",
  ],
  status: "shared",
};

// ── Other primitives ─────────────────────────────────────────────
export const PRIMITIVES = [
  {
    name: "IconButton",
    sites: [
      "components/layout/Topbar.jsx",
      "listings/pages/RCListingsPage.jsx",
    ],
  },
  {
    name: "Toggle",
    note: "Canonical export; implementation is ToggleButton.",
    sites: [
      "buyAlerts/RCBuyAlertsPage.jsx",
      "buyAlerts/components/FilterForms.jsx",
      "buyAlerts/components/RCAlertCard.jsx",
      "listings/components/RCListingCard.jsx",
      "listings/components/lookbook/LookbookToggleList.jsx",
      "listings/pages/RCListingsPage.jsx",
      "stock/templates/CoreListTable.jsx",
      "views/Leads.jsx",
    ],
  },
  {
    name: "SearchInput",
    sites: [
      "buyAlerts/RCBuyAlertsPage.jsx",
      "components/QuickFilter.jsx",
      "listings/components/lookbook/LookbookSearchBar.jsx",
      "listings/pages/RCListingsPage.jsx",
      "listings/pages/RCLookbooksPage.jsx",
      "stock/templates/CoreListInsights.jsx",
      "stock/templates/CoreListTable.jsx",
      "views/RCOverview/index.jsx",
      "views/UsersPage.jsx",
    ],
  },
  {
    name: "Badge",
    sites: [
      "buyAlerts/RCBuyAlertsPage.jsx",
      "listings/pages/RCListingsPage.jsx",
      "stock/components/StockStatePill.jsx",
      "stock/templates/CoreListInsights.jsx",
    ],
  },
  {
    name: "Input",
    note: "No direct JSX call sites, but SearchInput imports it — it's the field shell under the hood. KEEP.",
    sites: ["components/ui/SearchInput.jsx (dependency)"],
    forceStatus: "internal",
  },
  {
    name: "Tabs (components/ui)",
    note: "TRULY orphaned: re-exported by barrels but 0 consumers. Every tab strip imports Tabs from layouts/Default instead.",
    sites: [],
    forceStatus: "orphaned",
  },
  {
    name: "ToggleButton (components/ui)",
    note: "NOT orphaned — this is the implementation of Toggle (Toggle.jsx re-exports it). Powers every <Toggle>. KEEP.",
    sites: ["components/ui/Toggle.jsx (re-export)"],
    forceStatus: "internal",
  },
].map((p) => ({
  ...p,
  count: p.sites.length,
  status: p.forceStatus || statusFor(p.sites.length),
}));

// ── Legacy forks living OUTSIDE the design system ─────────────────
// These do the same job as a DS primitive but never adopted it. Surfaced so a
// "one place" refactor knows what still has to be migrated by hand.
export const LEGACY_FORKS = [
  {
    name: ".create button",
    where: "listings/atoms/LookbookTopButtons.jsx & ListingsTopButtons.jsx",
    doesJobOf: "Button (primary / hero CTA)",
    note: 'Old "New listing" buttons that predate <Button>. Not on the hero system.',
  },
  {
    name: "ToggleButton (fork)",
    where: "listings/atoms/ToggleButton.jsx",
    doesJobOf: "Toggle (components/ui)",
    note: "A parallel toggle used instead of the DS Toggle in some listing atoms.",
  },
  {
    name: "Tabs (layouts/Default)",
    where: "layouts/Default",
    doesJobOf: "Tabs (components/ui)",
    note: "Every tab strip imports this, not the DS Tabs primitive.",
  },
];

export const STATUS_META = {
  shared: { label: "Shared", tone: "warning", hint: "Change here ripples to many screens" },
  standalone: { label: "Standalone", tone: "success", hint: "One call site — safe to change in isolation" },
  unused: { label: "Unused", tone: "muted", hint: "Valid DS option, zero call sites" },
  internal: { label: "Internal dep", tone: "purple", hint: "No direct usage but powers another primitive — KEEP" },
  orphaned: { label: "Orphaned", tone: "danger", hint: "Defined but bypassed everywhere — safe to delete" },
  legacy: { label: "Legacy fork", tone: "danger", hint: "Lives outside the DS; needs manual migration" },
};
